import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import AdminModal from '../../components/AdminModal';
import {
  StandardTable, StandardTableBody, StandardTableCell, StandardTableHeader,
  StandardTableHeaderCell, StandardTableRow,
} from '../../../components/StandardTable';
import { HelpTrigger } from '../../../help';
import { apiError, labelClasses } from './InitiativeDetailPage';
import {
  bulkExplainerImport, selectBundleFiles,
  type BulkResultStatus, type ExplainerBulkResponse,
} from './explainerImport';

interface Props {
  cycle: string;
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

const STATUS_TONE: Record<BulkResultStatus, 'positive' | 'caution' | 'neutral' | 'info'> = {
  matched: 'info',
  imported: 'positive',
  unmatched: 'neutral',
  conflict: 'caution',
  failed: 'caution',
};
const STATUS_LABEL: Record<BulkResultStatus, string> = {
  matched: 'Ready',
  imported: 'Imported',
  unmatched: 'Unmatched',
  conflict: 'Conflict',
  failed: 'Failed',
};

/**
 * Import explainers — a whole cycle's folder at once (PRD v2 §5.15.5). One
 * dialog (the folder itself, or all its files), a preflight table of what each
 * bundle would do, then one Import that runs each matched measure in its own
 * transaction. A bad bundle never blocks the others.
 */
const ExplainerBulkImportModal: React.FC<Props> = ({ cycle, isOpen, onClose, onImported }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [result, setResult] = useState<ExplainerBulkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setFiles([]); setSkipped(0); setResult(null); setError(null); };
  const close = () => { reset(); onClose(); };

  const onFiles = (list: FileList | null) => {
    const all = Array.from(list ?? []);
    const bundles = selectBundleFiles(all);
    setFiles(bundles);
    setSkipped(all.length - bundles.length);
    setResult(null);
    setError(bundles.length ? null : 'No explainer files in that selection — expected *-fields.json and *-record-* files.');
  };

  const preview = useMutation({
    mutationFn: () => bulkExplainerImport(cycle, files, true),
    onSuccess: (data) => { setResult(data); setError(null); },
    onError: (err: any) => setError(apiError(err, 'Could not preview the folder')),
  });

  const run = useMutation({
    mutationFn: () => bulkExplainerImport(cycle, files, false),
    onSuccess: (data) => {
      setResult(data);
      const imported = data.results.filter((r) => r.status === 'imported').length;
      const failed = data.results.filter((r) => r.status === 'failed').length;
      toast[failed ? 'error' : 'success'](`${imported} imported${failed ? `, ${failed} failed` : ''} — publish the ones that are not yet published`);
      onImported();
    },
    onError: (err: any) => setError(apiError(err, 'Import failed')),
  });

  const ready = result?.results.filter((r) => r.status === 'matched').length ?? 0;
  const done = result && !result.dry_run;

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={close}
      title="Import explainers"
      subtitle={`Cycle ${cycle}`}
      size="xl"
      footer={<>
        <Button variant="secondary" size="sm" onClick={close}>{done ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button variant="primary" size="sm" onClick={() => preview.mutate()} disabled={files.length === 0 || preview.isPending} loading={preview.isPending}>
            Preview
          </Button>
        )}
        {result?.dry_run && (
          <Button variant="primary" size="sm" onClick={() => run.mutate()} disabled={ready === 0 || run.isPending} loading={run.isPending}>
            Import {ready} measure{ready === 1 ? '' : 's'}
          </Button>
        )}
      </>}
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Choose the explainer folder (or select all of its files). Bundles are matched to
            measures in this cycle by their Initiative number; scripts and packets are ignored.
          </p>
          <HelpTrigger contentKey="initiative-explainer-import" size="sm" ariaLabel="Help for importing explainers" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="explainer-folder" className={labelClasses}>Choose a folder</label>
            <input
              id="explainer-folder"
              type="file"
              multiple
              className="input w-full"
              onChange={(e) => onFiles(e.target.files)}
              {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
            />
          </div>
          <div>
            <label htmlFor="explainer-multi" className={labelClasses}>…or choose files</label>
            <input
              id="explainer-multi"
              type="file"
              multiple
              accept=".json,.pdf"
              className="input w-full"
              onChange={(e) => onFiles(e.target.files)}
            />
          </div>
        </div>
        {files.length > 0 && (
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            {files.length} explainer file{files.length === 1 ? '' : 's'} selected{skipped ? `, ${skipped} other file${skipped === 1 ? '' : 's'} ignored` : ''}.
          </p>
        )}

        {error && (
          <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
        )}

        {result && (
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Bundle</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Version</StandardTableHeaderCell>
                <StandardTableHeaderCell>Measure</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Files</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Result</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {result.results.map((r) => (
                <StandardTableRow key={r.prefix}>
                  <StandardTableCell>
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">{r.designation ?? r.prefix}</div>
                    <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{r.prefix}</div>
                  </StandardTableCell>
                  <StandardTableCell>{r.version ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    {r.initiative ? (
                      <>
                        <div className="text-text-primary dark:text-text-dark-primary">{r.initiative.label}</div>
                        <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          Initiative #{r.initiative.sos_initiative_number} · {r.initiative.publication_status}
                        </div>
                      </>
                    ) : (
                      <span className="text-text-tertiary dark:text-text-dark-tertiary">{r.filing_number ? `Initiative #${r.filing_number}` : '—'}</span>
                    )}
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      {[r.files.fields && 'fields', r.files.record && 'record', r.files.pdf && 'pdf'].filter(Boolean).join(' · ')}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <Chip label={STATUS_LABEL[r.status]} tone={STATUS_TONE[r.status]} size="sm" />
                    {r.reason && <div className="mt-1 max-w-xs text-xs text-text-tertiary dark:text-text-dark-tertiary">{r.reason}</div>}
                    {r.status === 'matched' && r.preflight?.warnings.length ? (
                      <div className="mt-1 max-w-xs text-xs text-text-tertiary dark:text-text-dark-tertiary">{r.preflight.warnings[0]}</div>
                    ) : null}
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
        {result?.ignored.length ? (
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Ignored: {result.ignored.join(', ')}</p>
        ) : null}
      </div>
    </AdminModal>
  );
};

export default ExplainerBulkImportModal;
